import React, { createContext, useContext, useEffect, useState } from "react";

const LangContext = createContext({ lang: "ar", setLang: () => {}, t: (ar) => ar, dir: "rtl" });

const COOKIE = "googtrans";
const setCookie = (v) => {
  const host = window.location.hostname;
  const parts = host.split(".");
  const root = parts.length > 2 ? "." + parts.slice(-2).join(".") : host;
  for (const d of ["", `; domain=${host}`, `; domain=${root}`]) {
    document.cookie = v ? `${COOKIE}=${v}; path=/${d}` : `${COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${d}`;
  }
};

// Whole-site English layer: our hand-written translations cover the key screens; everything else is machine-translated
// in place by the Google Translate element so no Arabic string is left untranslated for reviewers/partners.
const mountGoogle = () => {
  if (document.getElementById("gt-script")) return;
  if (!document.getElementById("google_translate_element")) {
    const el = document.createElement("div"); el.id = "google_translate_element"; el.style.display = "none"; document.body.appendChild(el);
  }
  window.googleTranslateElementInit = () => {
    try { new window.google.translate.TranslateElement({ pageLanguage: "ar", includedLanguages: "en", autoDisplay: false }, "google_translate_element"); } catch { /* ignore */ }
  };
  const s = document.createElement("script"); s.id = "gt-script"; s.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit"; s.async = true;
  document.body.appendChild(s);
};

// Brand names must never be machine-translated ("Smart trend"), and explicit dir="rtl" islands must flip to LTR.
const BRANDS = [
  [/^(رواج الذكية|rawaj smart|smart trend|smart vogue|smart rawaj|rawaj al-?thakiya|rawaj al-?dhakiya|smart promotion)$/i, "Rawaj Smart"],
  [/^(سوبر رواج|super rawaj|super fashion|super trend|super vogue|super promotion)$/i, "Super Rawaj"],
];
let fixTimer = null;
const fixEnglishDom = () => {
  fixTimer = null;
  document.querySelectorAll('[dir="rtl"]').forEach((el) => { if (el.tagName !== "INPUT" && el.tagName !== "TEXTAREA") el.setAttribute("dir", "ltr"); });
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT, {
    acceptNode: (el) => (el.textContent && el.textContent.trim().length <= 24 && !["SCRIPT", "STYLE", "INPUT", "TEXTAREA"].includes(el.tagName)
      && Array.from(el.children).every((c) => c.tagName === "FONT") ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
  });
  let el;
  while ((el = walker.nextNode())) {
    const txt = el.textContent.trim();
    for (const [re, en] of BRANDS) {
      if (re.test(txt)) {
        if (!el.classList.contains("notranslate")) { el.classList.add("notranslate"); el.setAttribute("translate", "no"); }
        if (txt !== en) el.textContent = en;
        break;
      }
    }
  }
};
const scheduleFix = () => { if (!fixTimer) fixTimer = setTimeout(fixEnglishDom, 250); };
let observer = null;
const startEnglishFixes = () => {
  if (observer) return;
  fixEnglishDom();
  observer = new MutationObserver(scheduleFix);
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
};

export const LangProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    const q = new URLSearchParams(window.location.search).get("lang");
    if (q === "en" || q === "ar") localStorage.setItem("rawaj_lang", q);
    return localStorage.getItem("rawaj_lang") || "ar";
  });
  const setLang = (l) => {
    localStorage.setItem("rawaj_lang", l);
    if (l === "en") { setCookie("/ar/en"); setLangState(l); mountGoogle(); }
    else { setCookie(""); setLangState(l); if (document.cookie.includes("googtrans") || document.documentElement.classList.contains("translated-ltr")) window.location.reload(); }
  };
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "en" ? "ltr" : "rtl";
    document.documentElement.classList.toggle("lang-en", lang === "en");
    if (lang === "en") { setCookie("/ar/en"); mountGoogle(); startEnglishFixes(); }
  }, [lang]);
  const t = (ar, en) => (lang === "en" && en ? en : ar);
  return <LangContext.Provider value={{ lang, setLang, t, dir: lang === "en" ? "ltr" : "rtl" }}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);
